/**
 * RÖSTEN — håll in, prata, släpp. Mikrofonen, inspelningen och anropet.
 *
 * "DU PRATAR. APPEN SKRIVER." Det är löftet på säljsidan, och det här är
 * kroken som håller det: pekaren ned startar telefonens mikrofon
 * (MediaRecorder), pekaren upp stoppar den, ljudet går base64-kodat till
 * VOICE_TRANSCRIBE och svaret — transkriptet och ett UTKAST — lämnas till
 * skärmen, som fyller i formuläret. INGENTING SPARAS HÄR. Hantverkaren tittar,
 * rättar och trycker Spara som vanligt; först då går en tidrad i utkorgen, med
 * source = 'voice'.
 *
 * LJUDET LEVER I MINNET TILLS SVARET KOMMIT och kastas sedan. Det skrivs aldrig
 * till någon lagring i telefonen, och servern sparar det inte heller
 * (VoiceTranscribeRequest i shared/types.ts).
 *
 * BARA ONLINE, INGEN KÖ (beslut 02). Knappen är av utan nät (ui/Knapp.tsx tar
 * `online`), och ett anrop som faller ligger inte kvar och skickas om — det
 * finns inget att skicka om, för utkastet är inte en rad. Felet visas, och
 * texten hänvisar till att knappa in för hand.
 *
 * MIKROFONTILLSTÅNDET är telefonens sak: första gången frågar den. Nekar
 * användaren blir läget 'nekad' med en text som säger VARFÖR knappen inte
 * fungerar och var man ändrar sig — den tystnar inte bara.
 *
 * LÄGENA är en diskriminerad union så att skärmen inte kan glömma ett: tsc
 * kräver en gren för varje status i texttabellen ROSTTEXT.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { VOICE_TRANSCRIBE } from '@delat/routes';
import { MAX_VOICE_AUDIO_BYTES, MAX_VOICE_SECONDS } from '@delat/types';
import type { CompanyId, VoiceTranscribeRequest, VoiceTranscribeResponse } from '@delat/types';
import { apifel, klienten } from '../../api';
import type { Klartidfel } from '../../api';
import { granskaRostsvar } from '../../api/granskare/rost';

export type Rostformat = VoiceTranscribeRequest['mime_type'];

export type Rostlage =
  | { readonly status: 'vilar' }
  | { readonly status: 'lyssnar'; readonly sekunder: number }
  | { readonly status: 'skickar' }
  | { readonly status: 'klar'; readonly svar: VoiceTranscribeResponse }
  | { readonly status: 'horde_inget' }
  | { readonly status: 'for_kort' }
  | { readonly status: 'nekad' }
  | { readonly status: 'ingen_mikrofon' }
  | { readonly status: 'stods_ej' }
  | { readonly status: 'fel'; readonly fel: Klartidfel };

export interface Rostinspelning {
  readonly lage: Rostlage;
  /** Pekaren ned på knappen. */
  readonly borja: () => void;
  /** Pekaren upp, eller lämnade knappen. Ofarlig att ropa utan inspelning. */
  readonly sluta: () => void;
  /** Tillbaka till 'vilar' — när formuläret ändras eller rutan stängs. */
  readonly nollstall: () => void;
}

/** Kortare än så är ett tryck, inte en mening. */
const MINSTA_INSPELNING_MS = 600;

/**
 * FORMATET TELEFONEN KAN SPELA IN I, i den ordning kontraktet räknar upp dem:
 * webm/Opus (Android Chrome), mp4/AAC (iPhone Safari), ogg/Opus (Firefox).
 * Codec-tillägget skickas till MediaRecorder; kontraktets bastyp skickas till
 * servern. null = ingen väg alls, och då säger knappen det.
 */
const FORMAT: readonly { readonly mime: Rostformat; readonly inspelning: string }[] = [
  { mime: 'audio/webm', inspelning: 'audio/webm;codecs=opus' },
  { mime: 'audio/mp4', inspelning: 'audio/mp4' },
  { mime: 'audio/ogg', inspelning: 'audio/ogg;codecs=opus' },
];

export const valjRostformat = (): { readonly mime: Rostformat; readonly inspelning: string } | null => {
  const Recorder = globalThis.MediaRecorder;
  if (Recorder === undefined || typeof Recorder.isTypeSupported !== 'function') return null;
  if (globalThis.navigator?.mediaDevices?.getUserMedia === undefined) return null;
  return FORMAT.find((f) => Recorder.isTypeSupported(f.inspelning)) ?? null;
};

/** Bytes -> base64, i bitar så att String.fromCharCode inte får en miljon argument. */
export const tillBase64 = (bytes: Uint8Array): string => {
  let binär = '';
  const STEG = 0x8000;
  for (let i = 0; i < bytes.length; i += STEG) {
    binär += String.fromCharCode(...bytes.subarray(i, i + STEG));
  }
  return btoa(binär);
};

export const useRostinspelning = (
  firma: CompanyId,
  vidSvar: (svar: VoiceTranscribeResponse) => void,
): Rostinspelning => {
  const [lage, sattLage] = useState<Rostlage>({ status: 'vilar' });

  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const bitar = useRef<Blob[]>([]);
  const startadVid = useRef(0);
  /** Mikrofonen håller på att öppnas (getUserMedia väntar). */
  const oppnar = useRef(false);
  /** Pekaren släpptes innan mikrofonen hann öppnas — stoppa så fort den gör det. */
  const slappTidigt = useRef(false);
  /** Ett anrop pågår. Referens och inte tillstånd: guarden får inte vara stale. */
  const skickar = useRef(false);
  const klocka = useRef<ReturnType<typeof setInterval> | null>(null);
  const vidSvarRef = useRef(vidSvar);
  vidSvarRef.current = vidSvar;

  const stangMikrofonen = useCallback((): void => {
    if (klocka.current !== null) {
      clearInterval(klocka.current);
      klocka.current = null;
    }
    for (const spår of stream.current?.getTracks() ?? []) spår.stop();
    stream.current = null;
    recorder.current = null;
  }, []);

  useEffect(() => stangMikrofonen, [stangMikrofonen]);

  const skicka = useCallback(
    async (blob: Blob, mime: Rostformat): Promise<void> => {
      if (blob.size > MAX_VOICE_AUDIO_BYTES) {
        // Samma tak som servern (FILE_TOO_LARGE), prövat INNAN uppladdningen:
        // ett ljud som ändå avvisas har redan kostat en uppladdning i trapphuset.
        sattLage({
          status: 'fel',
          fel: apifel('FILE_TOO_LARGE', { max_bytes: MAX_VOICE_AUDIO_BYTES }, ''),
        });
        return;
      }
      sattLage({ status: 'skickar' });
      skickar.current = true;
      try {
        const bytes = new Uint8Array(await blob.arrayBuffer());
        const svar = await klienten.anrop(
          VOICE_TRANSCRIBE,
          {
            params: { company_id: firma },
            data: { audio_base64: tillBase64(bytes), mime_type: mime },
          },
          granskaRostsvar,
        );
        if (!svar.ok) {
          sattLage({ status: 'fel', fel: svar.fel });
          return;
        }
        if (svar.varde.text === '') {
          sattLage({ status: 'horde_inget' });
          return;
        }
        sattLage({ status: 'klar', svar: svar.varde });
        vidSvarRef.current(svar.varde);
      } finally {
        skickar.current = false;
      }
    },
    [firma],
  );

  const borja = useCallback((): void => {
    if (recorder.current !== null || oppnar.current || skickar.current) return;
    const format = valjRostformat();
    if (format === null) {
      sattLage({ status: 'stods_ej' });
      return;
    }
    oppnar.current = true;
    slappTidigt.current = false;
    bitar.current = [];

    void globalThis.navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((ström) => {
        oppnar.current = false;
        if (slappTidigt.current) {
          for (const spår of ström.getTracks()) spår.stop();
          sattLage({ status: 'for_kort' });
          return;
        }
        stream.current = ström;
        const rec = new MediaRecorder(ström, { mimeType: format.inspelning });
        recorder.current = rec;
        rec.ondataavailable = (e) => {
          if (e.data.size > 0) bitar.current.push(e.data);
        };
        rec.onstop = () => {
          const längd = Date.now() - startadVid.current;
          const blob = new Blob(bitar.current, { type: format.inspelning });
          bitar.current = [];
          stangMikrofonen();
          if (längd < MINSTA_INSPELNING_MS || blob.size === 0) {
            sattLage({ status: 'for_kort' });
            return;
          }
          void skicka(blob, format.mime);
        };
        startadVid.current = Date.now();
        rec.start();
        sattLage({ status: 'lyssnar', sekunder: 0 });
        klocka.current = setInterval(() => {
          const sekunder = Math.floor((Date.now() - startadVid.current) / 1000);
          if (sekunder >= MAX_VOICE_SECONDS) {
            // Taket. Inspelningen stoppas och skickas som den är — hellre en
            // tolkning av det som sades än ett fel efter en hel minut.
            if (rec.state === 'recording') rec.stop();
            return;
          }
          sattLage({ status: 'lyssnar', sekunder });
        }, 250);
      })
      .catch((orsak: unknown) => {
        oppnar.current = false;
        const namn = orsak instanceof Error ? orsak.name : '';
        if (namn === 'NotAllowedError' || namn === 'SecurityError') {
          sattLage({ status: 'nekad' });
        } else if (namn === 'NotFoundError' || namn === 'OverconstrainedError') {
          sattLage({ status: 'ingen_mikrofon' });
        } else {
          sattLage({ status: 'stods_ej' });
        }
      });
  }, [skicka, stangMikrofonen]);

  const sluta = useCallback((): void => {
    const rec = recorder.current;
    if (rec === null) {
      // Mikrofonen har inte öppnats än: markera, så att then-grenen ovan stänger.
      if (oppnar.current) slappTidigt.current = true;
      return;
    }
    if (rec.state === 'recording') rec.stop();
  }, []);

  const nollstall = useCallback((): void => {
    if (recorder.current?.state === 'recording') recorder.current.stop();
    stangMikrofonen();
    sattLage({ status: 'vilar' });
  }, [stangMikrofonen]);

  return { lage, borja, sluta, nollstall };
};

/**
 * TEXTEN UNDER KNAPPEN, per läge. Skriven som en hantverkare talar: vad som
 * hände och vad han gör åt det. 'klar' har ingen text här — då visar skärmen
 * transkriptet i stället. 'fel' hämtar sin text ur felet, med ETT undantag:
 * SERVICE_UNAVAILABLE och SERVER_ERROR lovar i kontraktet att "det du sparat
 * ligger kvar och skickas automatiskt", och det är sant för utkorgen men inte
 * för rösten, som inte har någon kö. Där skriver skärmen sin egen rad, som
 * säger sanningen: skriv in för hand så länge.
 */
export const ROSTTEXT: Readonly<Record<Exclude<Rostlage['status'], 'klar' | 'fel'>, string>> = {
  vilar: 'Till exempel: "åtta timmar på Skolvägen, grävde dagvatten, fyra och en halv mil, servicebilen".',
  lyssnar: 'Lyssnar. Släpp när du är klar.',
  skickar: 'Tolkar det du sa ...',
  horde_inget: 'Hörde inget. Håll in knappen medan du pratar och släpp när du är klar.',
  for_kort: 'Det blev för kort. Håll in knappen medan du pratar.',
  nekad: 'Mikrofonen är blockerad för Klartid, så rösten kan inte höra dig. Tillåt mikrofonen i telefonens inställningar för webbläsaren — eller skriv in tiden för hand.',
  ingen_mikrofon: 'Hittar ingen mikrofon på den här enheten. Skriv in tiden för hand.',
  stods_ej: 'Den här webbläsaren kan inte spela in ljud i ett format appen förstår. Skriv in tiden för hand.',
};

/** Felen som INTE ska visa kontraktets kö-löfte — rösten har ingen kö. */
export const rostfeltext = (fel: Klartidfel): string =>
  fel.slag === 'api' && (fel.code === 'SERVICE_UNAVAILABLE' || fel.code === 'SERVER_ERROR')
    ? 'Rösten svarar inte just nu. Skriv in tiden för hand så länge — inget är förlorat.'
    : fel.text;
